import { Box, Skeleton } from "@mui/material";
import type { AppConfig } from "../../interfaces/appIntegration.interfaces";
import { useQueryClient } from "@tanstack/react-query";
import { useGetMerchantInfo } from "../../../paypal/actions/useGetMerchantInfo";
import { PaypalMerchantInfo } from "../../../paypal/components/MerchantInfo";

const APP_ID_PAYPAL = "paypal";

interface Props {
  app: AppConfig;
  isInstalled: boolean;
}

/**
 * Renders the integration dashboard (e.g. PayPal merchant info) when the app is connected.
 * Decoupled from the install button; uses full content width.
 */
export const AppDetailBody = ({ app, isInstalled }: Props) => {
  const queryClient = useQueryClient();

  if (!isInstalled) return null;

  if (app.id === APP_ID_PAYPAL) {
    return (
      <Box sx={{ width: "100%", bgcolor: "var(--fx-surface-base, #FFFFFF)", py: 2 }}>
        <Box sx={{ maxWidth: 1200, mx: "auto", px: 3 }}>
          <PaypalDashboardSection
            onDisconnected={() => {
              queryClient.invalidateQueries({ queryKey: ["merchant-info"] });
              queryClient.invalidateQueries({ queryKey: ["appStatus", app.id] });
            }}
          />
        </Box>
      </Box>
    );
  }

  return null;
};

/**
 * PayPal-specific dashboard. Fetches merchant info and renders PaypalMerchantInfo.
 */
function PaypalDashboardSection({ onDisconnected }: { onDisconnected: () => void }) {
  const { data: merchantInfo, isLoading } = useGetMerchantInfo();

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Skeleton variant="rounded" width="100%" height={120} animation="wave" sx={{ borderRadius: 2 }} />
        <Skeleton variant="rounded" width="100%" height={80} animation="wave" sx={{ borderRadius: 2 }} />
      </Box>
    );
  }

  if (!merchantInfo?.merchantId) return null;

  return <PaypalMerchantInfo merchantInfo={merchantInfo} onDisconnected={onDisconnected} />;
}
